import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import PriceHistoryModal from './PriceHistoryModal';

const DEFAULT_DAYS = 30;

const InvoicePriceReview = ({ selectedIds = [], onToggleItem, onHydrateStaged }) => {
  const { currentOrgId, user } = useAuth();
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(DEFAULT_DAYS));
  const [dateTo, setDateTo] = useState(() => isoToday());
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [exportedIds, setExportedIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      setError('');
      if (!currentOrgId) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('parsed_invoices')
          .select('id, invoice_number, vendor_name, vendor_account_ref, invoice_date, created_at, currency, net_amount, vat_amount, gross_amount, raw_extracted_data(data)')
          .eq('org_id', currentOrgId)
          .gte('created_at', new Date(dateFrom).toISOString())
          .lte('created_at', endOfDayIso(dateTo))
          .order('created_at', { ascending: false })
          .limit(30);
        if (error) throw error;
        const transformed = (data || []).map(r => {
          const lines = extractLines(r?.raw_extracted_data?.data);
          const header = {
            net: toNum(r?.net_amount),
            vat: toNum(r?.vat_amount),
            gross: toNum(r?.gross_amount)
          };
          const trust = computeInvoiceTrust(lines, header, r?.invoice_date);
          const basis = summarizeBasis(lines);
          return {
            id: r.id,
            invoiceNumber: r.invoice_number,
            vendor: r.vendor_name,
            invoiceDate: r.invoice_date || null,
            createdAt: r.created_at,
            currency: r.currency || 'GBP',
            lines,
            vendorAccountRef: r.vendor_account_ref || null,
            header,
            trust,
            basis
          };
        });
        setInvoices(transformed);
        const historyIds = Array.from(new Set(
          transformed.flatMap((inv) => (
            (inv.lines || [])
              .map((line) => line?.review?.price_history_id || line?.price_review?.price_history_id || null)
              .filter(Boolean)
          ))
        ));
        if (historyIds.length > 0) {
          const { data: historyRows, error: historyErr } = await supabase
            .from('epos_price_history')
            .select('id, export_id')
            .eq('org_id', currentOrgId)
            .in('id', historyIds);
          if (historyErr) throw historyErr;
          const staged = (historyRows || []).filter((row) => !row.export_id).map((row) => row.id);
          const exported = (historyRows || []).filter((row) => row.export_id).map((row) => row.id);
          setExportedIds(exported);
          if (typeof onHydrateStaged === 'function' && staged.length > 0) {
            onHydrateStaged(staged);
          }
        } else {
          setExportedIds([]);
        }
      } catch (e) {
        setError(e?.message || 'Failed to load invoices');
        setInvoices([]);
        setExportedIds([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrgId, dateFrom, dateTo, onHydrateStaged]);

  const [showDiscrepanciesOnly, setShowDiscrepanciesOnly] = useState(false);
  const [hideUntrusted, setHideUntrusted] = useState(false);

  const visibleInvoices = useMemo(() => {
    let arr = invoices;
    if (showDiscrepanciesOnly) arr = arr.filter(inv => !inv.trust?.reconciled || inv.trust?.allZeroLines);
    if (hideUntrusted) arr = arr.filter(inv => inv.trust?.trusted);
    return arr;
  }, [invoices, showDiscrepanciesOnly, hideUntrusted]);

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="grow min-w-[180px]">
          <Input label="From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="grow min-w-[180px]">
          <Input label="To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="flex items-center gap-4 self-start mt-[-4px]">
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input type="checkbox" checked={showDiscrepanciesOnly} onChange={(e) => setShowDiscrepanciesOnly(e.target.checked)} />
            Discrepancies only
          </label>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input type="checkbox" checked={hideUntrusted} onChange={(e) => setHideUntrusted(e.target.checked)} />
            Hide untrusted
          </label>
          <div className="text-sm text-muted-foreground">Showing latest 30 invoices in range</div>
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      {loading ? (
        <div className="text-sm">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="text-sm text-muted-foreground">No invoices found in this range.</div>
      ) : (
        <div className="space-y-6">
          {visibleInvoices.map(inv => (
            <InvoiceCard
              key={inv.id}
              invoice={inv}
              orgId={currentOrgId}
              userId={user?.id}
              selectedIds={selectedIds}
              onToggleItem={onToggleItem}
              exportedIds={exportedIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MATCH_TOLERANCE = 0;

const InvoiceCard = ({ invoice, orgId, userId, selectedIds, onToggleItem, exportedIds }) => {
  const [lines, setLines] = useState(invoice.lines || []);
  const [saving, setSaving] = useState(false);
  const [lineMeta, setLineMeta] = useState({});
  const [showHiddenLines, setShowHiddenLines] = useState(false);
  const [showStagedLines, setShowStagedLines] = useState(false);
  const [showExportedLines, setShowExportedLines] = useState(false);

  useEffect(() => {
    setLines(invoice.lines || []);
    setLineMeta({});
    setShowHiddenLines(false);
    setShowStagedLines(false);
    setShowExportedLines(false);
  }, [invoice]);

  const handleLineMeta = useCallback((idx, meta) => {
    setLineMeta((prev) => {
      const next = { ...prev };
      if (meta == null) delete next[idx];
      else next[idx] = meta;
      return next;
    });
  }, []);

  const lineMatchesWithinTolerance = useCallback((idx) => {
    const meta = lineMeta[idx];
    if (!meta) return false;
    const { invEach, eposEach, hasMatch } = meta;
    if (!hasMatch) return false;
    if (invEach == null || eposEach == null) return false;
    return Math.abs(eposEach - invEach) <= MATCH_TOLERANCE;
  }, [lineMeta]);

  const hiddenMask = useMemo(() => (
    lines.map((_, idx) => lineMatchesWithinTolerance(idx))
  ), [lines, lineMatchesWithinTolerance]);

  const hiddenCount = hiddenMask.filter(Boolean).length;
  const allHidden = hiddenCount === lines.length && lines.length > 0;
  const stagedMask = useMemo(() => (
    lines.map((line) => {
      const id = line?.review?.price_history_id || line?.price_review?.price_history_id || null;
      return id && (selectedIds || []).includes(id);
    })
  ), [lines, selectedIds]);
  const stagedCount = stagedMask.filter(Boolean).length;
  const exportedMask = useMemo(() => (
    lines.map((line) => {
      const id = line?.review?.price_history_id || line?.price_review?.price_history_id || null;
      return id && (exportedIds || []).includes(id);
    })
  ), [lines, exportedIds]);
  const exportedCount = exportedMask.filter(Boolean).length;
  const varianceMask = useMemo(() => (
    lines.map((_, idx) => !hiddenMask[idx])
  ), [lines, hiddenMask]);
  const varianceCount = varianceMask.filter(Boolean).length;
  const stagedVarianceCount = stagedMask.reduce((acc, staged, idx) => (
    acc + ((staged && varianceMask[idx]) ? 1 : 0)
  ), 0);
  const exportedVarianceCount = exportedMask.reduce((acc, exported, idx) => (
    acc + ((exported && varianceMask[idx]) ? 1 : 0)
  ), 0);
  const showSuccessState = varianceCount > 0 && stagedVarianceCount === varianceCount && !showStagedLines;
  const showExportedSuccessState = varianceCount > 0 && exportedVarianceCount === varianceCount && !showExportedLines;

  const saveLine = async (idx, payload, matchInfo) => {
    setSaving(true);
    const reviewPayload = { ...payload };
    let historyId = reviewPayload.price_history_id || null;

    const updateRawReview = async (nextPayload) => {
      const { data: rawRow, error: fetchErr } = await supabase
        .from('raw_extracted_data')
        .select('data')
        .eq('invoice_id', invoice.id)
        .maybeSingle();
      if (fetchErr) throw fetchErr;
      const cur = rawRow?.data?.current || {};
      const li = Array.isArray(cur.line_items) ? [...cur.line_items] : [];
      const existing = li[idx] || {};
      li[idx] = { ...existing, price_review: nextPayload };
      const newData = { ...rawRow?.data, current: { ...cur, line_items: li } };
      const { error: upd } = await supabase
        .from('raw_extracted_data')
        .update({ data: newData })
        .eq('invoice_id', invoice.id);
      if (upd) throw upd;
    };
    try {
      const updated = [...lines];
      updated[idx] = { ...updated[idx], review: reviewPayload };
      setLines(updated);

      // Persist review info into raw_extracted_data.current.line_items[idx].price_review
      await updateRawReview(reviewPayload);

      // If a product match was selected, write history for staging (even when price is unchanged)
      if (matchInfo?.sku && isFinite(matchInfo?.newPriceEach)) {
        const newPrice = Number(matchInfo.newPriceEach);
        const oldPrice = isFinite(matchInfo?.oldPriceEach) ? Number(matchInfo.oldPriceEach) : null;
        const priceChanged = oldPrice == null || Math.abs(newPrice - oldPrice) >= 0.0001;
        const shouldWriteHistory = !historyId || priceChanged;
        let productId = matchInfo?.productId || null;

        if (priceChanged) {
          // Update epos_products and return product id
          const { data: prodUpdate, error: prodErr } = await supabase
            .from('epos_products')
            .update({ current_price: newPrice, last_validated_at: new Date().toISOString(), updated_by: userId })
            .eq('org_id', orgId)
            .eq('sku', matchInfo.sku)
            .select('id')
            .maybeSingle();
          if (prodErr) throw prodErr;
          productId = prodUpdate?.id || productId;

          // Audit log entry
          await supabase
            .from('audit_log')
            .insert({
              entity_type: 'epos_products',
              entity_id: productId,
              action: 'price_updated',
              user_id: userId,
              org_id: orgId,
              meta_json: {
                sku: matchInfo.sku,
                old_price: oldPrice,
                new_price: newPrice,
                invoice_id: invoice.id,
                reason: 'manual_review'
              }
            });
        }

        if (shouldWriteHistory) {
          if (!productId) {
            const { data: prodRow, error: prodLookupErr } = await supabase
              .from('epos_products')
              .select('id')
              .eq('org_id', orgId)
              .eq('sku', matchInfo.sku)
              .maybeSingle();
            if (prodLookupErr) throw prodLookupErr;
            productId = prodRow?.id || null;
          }
          if (!productId) {
            throw new Error(`Unable to locate product for SKU ${matchInfo.sku}`);
          }

          // Insert price history (even if the price did not change) so the line can be staged
          const { data: historyRow, error: histErr } = await supabase
            .from('epos_price_history')
            .insert({
              org_id: orgId,
              product_id: productId,
              sku: matchInfo.sku,
              old_price: oldPrice,
              new_price: newPrice,
              changed_by: userId,
              invoice_id: invoice.id,
              source: 'manual_review'
            })
            .select('id')
            .maybeSingle();
          if (histErr) throw histErr;
          if (historyRow?.id) {
            historyId = historyRow.id;
            reviewPayload.price_history_id = historyRow.id;
            setLines((prev) => {
              const next = [...prev];
              const current = next[idx] || {};
              next[idx] = { ...current, review: { ...reviewPayload } };
              return next;
            });
            await updateRawReview(reviewPayload);
          }
        }
      }

      // Save EPOS match rule for future auto-matching (exact pattern of line description)
      if (matchInfo?.sku) {
        try {
          // Prefer using loaded vendorAccountRef for scoping (optional)
          const vendorAccountRef = invoice?.vendorAccountRef || null;

          const normalized = normalizeDesc(lines[idx]?.description);
          if (normalized && normalized.length >= 3) {
            await supabase
              .from('epos_match_rules')
              .insert({
                org_id: orgId,
                vendor_account_ref: vendorAccountRef,
                pattern: normalized,
                pattern_type: 'exact',
                case_sensitive: false,
                exact_match: true,
                confidence_threshold: 1.0,
                priority: 10,
                target_sku: matchInfo.sku,
                created_by: userId,
                enabled: true
              });
          }
        } catch (e) {
          // Non-fatal
          console.warn('Failed to save epos_match_rule:', e?.message || e);
        }
      }

      return historyId;
    } catch (e) {
      alert(e?.message || 'Failed to save');
      return null;
    } finally { setSaving(false); }
  };

  if (showExportedSuccessState) {
    return (
      <div className="border border-slate-200 bg-slate-50/60 rounded-md px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon name="PackageCheck" size={18} className="text-slate-600" />
          <div>
            <div className="text-sm font-medium text-foreground">All variances exported</div>
            <div className="text-xs text-muted-foreground">
              {invoice.invoiceNumber || invoice.id} • {invoice.vendor}
            </div>
          </div>
        </div>
        {exportedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowExportedLines(true)}
            className="text-xs text-slate-600 hover:text-slate-800 transition-smooth"
          >
            Show exported items
          </button>
        )}
      </div>
    );
  }

  if (showSuccessState) {
    return (
      <div className="border border-emerald-200 bg-emerald-50/60 rounded-md px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon name="CheckCircle2" size={18} className="text-emerald-600" />
          <div>
            <div className="text-sm font-medium text-foreground">All variances resolved</div>
            <div className="text-xs text-muted-foreground">
              {invoice.invoiceNumber || invoice.id} • {invoice.vendor}
            </div>
          </div>
        </div>
        {stagedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowStagedLines(true)}
            className="text-xs text-emerald-700 hover:text-emerald-900 transition-smooth"
          >
            Show staged items
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="px-4 py-3 bg-muted flex items-center justify-between">
        <div className="text-sm flex items-center gap-2 flex-wrap">
          <a
            href={`/invoice-editor?id=${invoice.id}&view=1`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary hover:underline"
            title="Open invoice in a new tab"
          >
            {invoice.invoiceNumber || invoice.id}
          </a>
          <span className="text-muted-foreground"> • {invoice.vendor}</span>
          <span className="text-muted-foreground"> • {new Date(invoice.createdAt).toLocaleString('en-GB')}</span>
          <span
            className="text-xs px-2 py-1 rounded border border-border text-foreground bg-background/60"
            title={`Basis: ${invoice?.basis?.default?.toUpperCase() || 'MIXED'}\nEach: ${invoice?.basis?.summary?.each || 0} • Case: ${invoice?.basis?.summary?.case || 0} • Unknown: ${invoice?.basis?.summary?.unknown || 0}`}
          >
            Basis: {invoice?.basis?.default || 'mixed'}
          </span>
          {!invoice?.trust?.trusted && (
            <span
              className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 bg-amber-50"
              title={`Untrusted: ${invoice?.trust?.reason || 'reconcile_failed'}\nΔ Net: £${(invoice?.trust?.deltas?.net || 0).toFixed(2)} • Δ VAT: £${(invoice?.trust?.deltas?.vat || 0).toFixed(2)} • Δ Gross: £${(invoice?.trust?.deltas?.gross || 0).toFixed(2)}`}
            >
              Untrusted totals
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">Currency: {invoice.currency || 'GBP'}</div>
      </div>
      <div className="overflow-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-muted">
              <Th className="w-10 text-center">Stage</Th>
              <Th>Description</Th>
              <Th className="text-right">Qty (cases)</Th>
              <Th className="text-right">Unit £ (case)</Th>
              <Th className="text-right">Case Qty</Th>
              <Th>Unit Size</Th>
              <Th>Match</Th>
              <Th className="text-right">EPOS £ (each)</Th>
              <Th className="text-right">Inv £ (each)</Th>
              <Th className="text-right">Δ</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, idx) => (
              <LineRow
                key={`line-${idx}`}
                idx={idx}
                line={ln}
                orgId={orgId}
                onSave={saveLine}
                saving={saving}
                vendorAccountRef={invoice.vendorAccountRef}
                vendorName={invoice.vendor}
                isTrusted={invoice?.trust?.trusted}
                trustReason={invoice?.trust?.reason}
                onMetaChange={handleLineMeta}
                isHidden={(Boolean(hiddenMask[idx]) && !showHiddenLines) || (stagedMask[idx] && !showStagedLines) || (exportedMask[idx] && !showExportedLines)}
                selectedIds={selectedIds}
                onToggleItem={onToggleItem}
                isExported={Boolean(exportedMask[idx])}
              />
            ))}
            {allHidden && !showHiddenLines && (
              <tr>
                <td colSpan={11} className="px-3 py-4 text-center text-muted-foreground">
                  All lines match EPOS exactly. Use the button below to reveal them.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {hiddenCount > 0 && (
        <div className="border-t border-border bg-background/60">
          <button
            type="button"
            onClick={() => setShowHiddenLines((prev) => !prev)}
            className="w-full px-4 py-2 text-sm text-muted-foreground flex items-center justify-between hover:text-foreground transition-smooth"
          >
            <span>
              {showHiddenLines ? 'Hide' : 'Show'} {hiddenCount} additional matched line{hiddenCount === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-muted-foreground">{showHiddenLines ? '-' : '+'}</span>
          </button>
        </div>
      )}
      {stagedCount > 0 && (
        <div className="border-t border-border bg-background/60">
          <button
            type="button"
            onClick={() => setShowStagedLines((prev) => !prev)}
            className="w-full px-4 py-2 text-sm text-muted-foreground flex items-center justify-between hover:text-foreground transition-smooth"
          >
            <span className="flex items-center gap-2">
              <Icon name={showStagedLines ? 'EyeOff' : 'Eye'} size={14} />
              {showStagedLines ? 'Hide' : 'Show'} {stagedCount} staged item{stagedCount === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-muted-foreground">{showStagedLines ? '-' : '+'}</span>
          </button>
        </div>
      )}
      {exportedCount > 0 && (
        <div className="border-t border-border bg-background/60">
          <button
            type="button"
            onClick={() => setShowExportedLines((prev) => !prev)}
            className="w-full px-4 py-2 text-sm text-muted-foreground flex items-center justify-between hover:text-foreground transition-smooth"
          >
            <span className="flex items-center gap-2">
              <Icon name={showExportedLines ? 'EyeOff' : 'Eye'} size={14} />
              {showExportedLines ? 'Hide' : 'Show'} {exportedCount} exported item{exportedCount === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-muted-foreground">{showExportedLines ? '-' : '+'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const LineRow = ({
  idx,
  line,
  orgId,
  onSave,
  saving,
  vendorAccountRef,
  vendorName,
  isTrusted,
  trustReason,
  onMetaChange,
  isHidden = false,
  selectedIds,
  onToggleItem,
  isExported = false
}) => {
  const [term, setTerm] = useState(suggestFromLine(line));
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editedPrice, setEditedPrice] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  // Hydrate selection from previously approved review payload (if any)
  useEffect(() => {
    const saved =
      line?.review ||
      line?.price_review ||
      null;
    const savedSku = saved?.matched_sku;
    if (savedSku) {
      // High confidence auto-match: pre-select the product
      setSelected({
        id: saved?.product_id || null,
        sku: savedSku,
        name: saved?.matched_name || savedSku,
        supplier_code: saved?.supplier_code || null,
        current_price: saved?.epos_price_each ?? saved?.new_price_each ?? null
      });
      setEditedPrice('');
    } else if (saved?.suggested_sku) {
      // Low confidence suggestion: pre-fill search term only
      setTerm(saved.suggested_sku);
    }
  }, [line?.review, line?.price_review]);

  // On mount, try to auto-apply a saved rule (exact match on normalized description)
  useEffect(() => {
    const tryAutoMatch = async () => {
      const norm = normalizeDesc(line?.description);
      // Skip auto-match when a saved SKU already exists
      if (!norm || !orgId || line?.review?.matched_sku || line?.price_review?.matched_sku) return;
      try {
        let sku = null;
        // Prefer vendor-scoped rule
        if (vendorAccountRef) {
          const { data: r1, error: e1 } = await supabase
            .from('epos_match_rules')
            .select('target_sku')
            .eq('org_id', orgId)
            .eq('enabled', true)
            .eq('pattern', norm)
            .eq('vendor_account_ref', vendorAccountRef)
            .limit(1);
          if (e1) throw e1;
          sku = r1?.[0]?.target_sku || null;
        }
        // Fallback to global rule (no vendor)
        if (!sku) {
          const { data: r2, error: e2 } = await supabase
            .from('epos_match_rules')
            .select('target_sku')
            .eq('org_id', orgId)
            .eq('enabled', true)
            .eq('pattern', norm)
            .is('vendor_account_ref', null)
            .limit(1);
          if (e2) throw e2;
          sku = r2?.[0]?.target_sku || null;
        }
        if (sku) {
          const { data: prod, error: pErr } = await supabase
            .from('epos_products')
            .select('id, sku, name, supplier_code, current_price')
            .eq('org_id', orgId)
            .eq('sku', sku)
            .maybeSingle();
          if (!pErr && prod) {
            setSelected(prod);
          }
        }
      } catch {}
    };
    tryAutoMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const find = async () => {
    if (!term || !orgId) return;
    setLoading(true);
    try {
      let resolvedSupplier = null;
      if (vendorName) {
        const { data: sc } = await supabase.rpc('resolve_supplier_code', {
          p_org_id: orgId,
          p_vendor_name: vendorName
        });
        resolvedSupplier = sc || null;
      }
      const { data, error } = await supabase
        .rpc('search_products_fuzzy', {
          search_term: term,
          p_org_id: orgId,
          p_supplier_code: resolvedSupplier
        });

      if (error) throw error;
      setCands(data || []);
    } catch (e) {
      console.error('Search failed:', e.message);
      setCands([]);
    } finally {
      setLoading(false);
    }
  };

  const computedPrices = useMemo(() => {
    const invoicePricing = computeInvoicePricing(line);
    const { caseQty, invCase, invEach } = invoicePricing;
    const eposBase = roundCurrency(toNum(selected?.current_price || 0));
    const eposEachRaw = editedPrice !== '' ? toNum(editedPrice) : eposBase;
    const eposEach = roundCurrency(eposEachRaw);
    let delta = null;
    if (invEach != null && eposEach != null) {
      // Red/green semantics: positive is good (invoice under EPOS)
      // Define Δ = EPOS(each) - Invoice(each)
      const rawDiff = eposEach - invEach;
      const diff = roundCurrency(rawDiff);
      const pct = invEach !== 0 ? (diff / invEach) * 100 : null;
      delta = { diff, pct };
    }
    return { caseQty, invCase, invEach, eposEach, delta, base: eposBase };
  }, [selected, line, editedPrice]);

  useEffect(() => {
    if (!onMetaChange) return;
    onMetaChange(idx, {
      invEach: computedPrices.invEach,
      eposEach: computedPrices.eposEach,
      hasMatch: Boolean(selected || line?.review?.matched_sku)
    });
  }, [idx, onMetaChange, computedPrices.invEach, computedPrices.eposEach, selected, line?.review?.matched_sku]);

  useEffect(() => {
    return () => {
      onMetaChange?.(idx, null);
    };
  }, [idx, onMetaChange]);

  const approve = async () => {
    if (!selected) return;
    if (!isTrusted) {
      const ok = window.confirm('Header totals do not reconcile with line sums for this invoice. Proceed with price approval?');
      if (!ok) return;
    }
    const existingHistoryId = line?.review?.price_history_id || line?.price_review?.price_history_id || null;
    const payload = {
      matched_sku: selected.sku,
      matched_name: selected.name,
      epos_price_each: computedPrices.eposEach,
      invoice_unit_case: computedPrices.invCase,
      invoice_unit_each: computedPrices.invEach,
      case_quantity: computedPrices.caseQty ?? null,
      unit_size: line?.unitSize ?? line?.unit_size ?? null,
      delta_abs: computedPrices.delta?.diff ?? null,
      delta_pct: computedPrices.delta?.pct ?? null,
      trusted: !!isTrusted,
      untrusted_reason: isTrusted ? null : (trustReason || 'lines_totals_mismatch'),
      approved_at: new Date().toISOString(),
      ...(existingHistoryId ? { price_history_id: existingHistoryId } : {})
    };
    const savedHistoryId = await onSave(idx, payload, {
      sku: selected.sku,
      productId: selected.id,
      oldPriceEach: computedPrices.base,
      newPriceEach: computedPrices.eposEach
    });

    const nextHistoryId = savedHistoryId || existingHistoryId;
    if (nextHistoryId && onToggleItem && !(selectedIds || []).includes(nextHistoryId)) {
      onToggleItem(nextHistoryId);
    }
  };

  const priceHistoryId = line?.review?.price_history_id || line?.price_review?.price_history_id || null;
  const isStaged = priceHistoryId ? (selectedIds || []).includes(priceHistoryId) : false;
  const canStage = Boolean(priceHistoryId) && typeof onToggleItem === 'function' && !isExported;
  const highlightUntrusted = !isTrusted && !(line?.review?.approved_at);
  return (
    <>
    <tr
      className={`border-t border-border ${highlightUntrusted ? 'bg-rose-50' : ''} ${isStaged ? 'bg-emerald-50/50' : ''} ${isExported ? 'bg-slate-50/70' : ''} ${isHidden ? 'hidden' : ''}`}
      title={highlightUntrusted ? 'Invoice totals do not reconcile; review this line before approval' : ''}
    >
      <Td className="text-center">
        <input
          type="checkbox"
          checked={isStaged}
          disabled={!canStage}
          onChange={() => {
            if (priceHistoryId && onToggleItem) {
              onToggleItem(priceHistoryId);
            }
          }}
          title={
            isExported
              ? 'Already exported'
              : (canStage ? 'Stage this price change for export' : 'Approve this line to enable staging')
          }
        />
      </Td>
      <Td>{line?.description || ''}</Td>
      <Td num>{toNum(line?.quantity) ?? ''}</Td>
      <Td num>{fmtMoney(computedPrices.invCase)}</Td>
      <Td num>{computedPrices.caseQty ?? ''}</Td>
      <Td>{line?.unitSize || line?.unit_size || ''}</Td>
      <Td>
        <div className="flex items-center gap-2">
          <input
            className="px-2 py-1 text-xs border border-border rounded bg-background"
            placeholder="SKU or name"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={find} loading={loading}>Find</Button>
          <select
            className="text-xs border border-border rounded bg-background"
            value={selected?.sku || ''}
            onChange={(e) => {
              const nextSku = e.target.value;
              if (!nextSku) {
                setSelected(null);
                return;
              }
              const match = cands.find(c => c.sku === nextSku);
              if (match) setSelected(match);
              else if (!selected || selected.sku !== nextSku) {
                setSelected(prev => prev && prev.sku === nextSku ? prev : { ...prev, sku: nextSku });
              }
            }}
          >
            <option value="">Select match…</option>
            {selected && !cands.some(c => c.sku === selected.sku) && (
              <option value={selected.sku}>
                {selected.sku} — {selected.name || 'Saved match'}
              </option>
            )}
            {cands.map(c => (
              <option key={c.id} value={c.sku}>{c.sku} — {c.name}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            disabled={!selected?.sku}
            title="Show price history"
            iconName="History"
          >
            History
          </Button>
        </div>
      </Td>
      <Td num>
        {selected ? (
          <input
            className="w-24 px-2 py-1 text-xs border border-border rounded bg-background text-right"
            type="number"
            step="0.01"
            min="0"
            value={
              editedPrice === ''
                ? (Number.isFinite(computedPrices.base) ? Number(computedPrices.base).toFixed(2) : '')
                : editedPrice
            }
            onFocus={() => {
              if (editedPrice === '' && Number.isFinite(computedPrices.base)) {
                setEditedPrice(Number(computedPrices.base).toFixed(2));
              }
            }}
            onChange={(e) => setEditedPrice(e.target.value)}
          />
        ) : ''}
      </Td>
      <Td num title={(toNum(line?.unitPrice) === 0 && toNum(line?.total) === 0 && toNum(line?.netAmount) === 0) ? 'Line has zero monetary values' : ''}>
        {computedPrices.invEach != null ? (
          <span className={(toNum(line?.unitPrice) === 0 && toNum(line?.total) === 0 && toNum(line?.netAmount) === 0) ? 'inline-flex items-center gap-1 text-amber-600' : ''}>
            {(toNum(line?.unitPrice) === 0 && toNum(line?.total) === 0 && toNum(line?.netAmount) === 0) && (
              <Icon name="AlertCircle" size={14} />
            )}
            {fmtMoney(computedPrices.invEach)}
          </span>
        ) : ''}
      </Td>
      <Td num>
        {computedPrices.delta ? (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              computedPrices.delta.diff > 0
                ? 'text-success'
                : computedPrices.delta.diff < 0
                ? 'text-destructive'
                : 'text-muted-foreground'
            }`}
            title={`Δ = EPOS(each) − Inv(each)`}
          >
            <Icon
              name={
                computedPrices.delta.diff > 0
                  ? 'TrendingDown' // invoice lower than EPOS = good
                  : computedPrices.delta.diff < 0
                  ? 'TrendingUp'
                  : 'Minus'
              }
              size={14}
            />
            {computedPrices.delta.diff > 0 ? '+' : ''}{fmtMoney(computedPrices.delta.diff)}
            {computedPrices.delta.pct != null ? ` (${computedPrices.delta.pct > 0 ? '+' : ''}${computedPrices.delta.pct.toFixed(1)}%)` : ''}
          </span>
        ) : ''}
      </Td>
      <Td>
        <Button variant="success" size="sm" onClick={approve} disabled={!selected || saving}>Approve</Button>
      </Td>
    </tr>
    {/* Price History Modal */}
    <PriceHistoryModal
      isOpen={historyOpen}
      onClose={() => setHistoryOpen(false)}
      orgId={orgId}
      sku={selected?.sku}
    />
    </>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false, className = '' }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-xs' : ''} ${num ? 'text-right' : ''} ${className}`}>{children}</td>
);

function computeInvoicePricing(line = {}) {
  const caseQty = toNum(line?.caseQuantity ?? line?.case_quantity);
  const persistedEach = toNum(
    line?.review?.invoice_unit_each ??
    line?.price_review?.invoice_unit_each ??
    null
  );
  const persistedCase = toNum(
    line?.review?.invoice_unit_case ??
    line?.price_review?.invoice_unit_case ??
    null
  );
  if (persistedEach != null) {
    const invEach = persistedEach;
    const invCase = persistedCase != null
      ? persistedCase
      : (caseQty && caseQty > 0 ? invEach * caseQty : invEach);
    return { caseQty, invCase, invEach };
  }

  const qty = toNum(line?.quantity);
  const upRaw = toNum(line?.unitPrice ?? line?.unit_price ?? line?.unit ?? null);
  const netRep = toNum(line?.netAmount ?? line?.net_amount);
  const basisHint = (line?.priceBasis === 'case' || line?.priceBasis === 'each') ? line?.priceBasis : null;

  const toP = (x) => Math.round((Number(x) || 0) * 100);
  const unitsPerCase = (caseQty && caseQty > 0) ? caseQty : 1;
  const netEachHyp = (qty && upRaw != null) ? qty * unitsPerCase * upRaw : null;
  const netCaseHyp = (qty && upRaw != null) ? qty * upRaw : null;
  const dEach = (netRep != null && netEachHyp != null) ? Math.abs(toP(netRep) - toP(netEachHyp)) : null;
  const dCase = (netRep != null && netCaseHyp != null) ? Math.abs(toP(netRep) - toP(netCaseHyp)) : null;

  let basis = basisHint;
  if (!basis) {
    if (dEach != null && dCase != null) {
      basis = dCase <= dEach ? 'case' : 'each';
    } else if (caseQty && caseQty > 0) {
      basis = 'case';
    } else {
      basis = 'each';
    }
  }

  const invCaseRaw = basis === 'each'
    ? (upRaw != null && caseQty ? upRaw * caseQty : upRaw)
    : upRaw;
  const invEachRaw = basis === 'case'
    ? (invCaseRaw != null && caseQty ? invCaseRaw / caseQty : invCaseRaw)
    : upRaw;
  const invCase = roundCurrency(invCaseRaw);
  const invEach = roundCurrency(invEachRaw);
  return { caseQty, invCase, invEach };
}

function roundCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function extractLines(data) {
  const cur = data?.current || data?.parsed_json || data || {};
  const arr = Array.isArray(cur?.line_items) ? cur.line_items : [];
  return arr.map(li => ({
    description: li?.description || '',
    quantity: li?.quantity ?? li?.qty ?? null,
    unitPrice: roundCurrency(li?.unit_price ?? li?.unitPrice),
    netAmount: roundCurrency(li?.net_amount),
    vatAmount: roundCurrency(li?.vat_amount),
    caseQuantity: li?.case_quantity ?? null,
    unitSize: li?.unit_size ?? null,
    total: roundCurrency(li?.total),
    review: li?.price_review || null
  }));
}

function summarizeBasis(lines = []) {
  let each = 0, kase = 0, unknown = 0;
  for (const li of lines) {
    const b = li?.priceBasis;
    if (b === 'each') each++;
    else if (b === 'case') kase++;
    else unknown++;
  }
  let def = 'mixed';
  if (each > 0 && kase === 0 && unknown === 0) def = 'each';
  else if (kase > 0 && each === 0 && unknown === 0) def = 'case';
  else if (each === 0 && kase === 0 && unknown > 0) def = 'mixed';
  else if (each > kase) def = 'each';
  else if (kase > each) def = 'case';
  else def = 'mixed';
  return { default: def, summary: { each, case: kase, unknown } };
}

function computeInvoiceTrust(lines, header, invoiceDate) {
  const sums = (lines || []).reduce((acc, li) => ({
    net: acc.net + (toNum(li?.netAmount) || 0),
    vat: acc.vat + (toNum(li?.vatAmount) || 0),
    gross: acc.gross + (toNum(li?.total) || 0),
    zeros: acc.zeros + ((toNum(li?.unitPrice) === 0 && toNum(li?.netAmount) === 0 && toNum(li?.total) === 0) ? 1 : 0),
  }), { net: 0, vat: 0, gross: 0, zeros: 0 });
  const deltas = {
    net: Math.abs((toNum(header?.net) || 0) - sums.net),
    vat: Math.abs((toNum(header?.vat) || 0) - sums.vat),
    gross: Math.abs((toNum(header?.gross) || 0) - sums.gross),
  };
  const reconciled = deltas.net < 0.01 && deltas.vat < 0.01 && deltas.gross < 0.01;
  const allZeroLines = (lines?.length || 0) > 0 && sums.zeros === lines.length;

  const trusted = reconciled && !allZeroLines;
  let reason = null;
  if (!trusted) {
    if (!reconciled) reason = 'lines_totals_mismatch';
    else if (allZeroLines) reason = 'zero_lines';
  }

  return { trusted, reconciled, deltas, allZeroLines, reason };
}

function toNum(v) { if (v === undefined || v === null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function fmtMoney(v) { if (v == null) return ''; const n = Number(v); if (!Number.isFinite(n)) return ''; return `£${n.toFixed(2)}`; }
function isoDaysAgo(d) { const dt = new Date(); dt.setDate(dt.getDate() - d); return dt.toISOString().slice(0,10); }
function isoToday() { return new Date().toISOString().slice(0,10); }
function endOfDayIso(d) { const dt = new Date(d); dt.setHours(23,59,59,999); return dt.toISOString(); }
function suggestFromLine(line) {
  const s = String(line?.description || '').trim();
  const m = s.match(/\b(\d{6,14})\b/); // look for a code-like token
  return m ? m[1] : s.split(' ').slice(0,4).join(' ');
}

function normalizeDesc(s) {
  try {
    return String(s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^a-z0-9%&()\/\-., ]/g, '')
      .trim();
  } catch { return ''; }
}

export default InvoicePriceReview;
